import type { ComponentChildren, Ref } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { Icon, type IconName } from './Icon';

export interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  icon?: IconName;
  /** Keyboard hint shown at the end ("ENTER"). */
  hint?: string;
  disabled?: boolean;
  title?: string;
  class?: string;
  type?: 'button' | 'submit';
  onClick?: (e: MouseEvent) => void;
  children?: ComponentChildren;
  buttonRef?: Ref<HTMLButtonElement>;
  autoFocus?: boolean;
  'aria-label'?: string;
  'aria-pressed'?: boolean;
}

export function Button({ variant = 'secondary', size = 'md', icon, hint, disabled, title, class: cls, type = 'button', onClick, children, buttonRef, autoFocus, ...aria }: ButtonProps) {
  const own = useRef<HTMLButtonElement>(null);
  // the autofocus attribute only works on page load: focus when mounted
  useEffect(() => {
    if (autoFocus) own.current?.focus({ preventScroll: true });
  }, []);
  return (
    <button
      ref={(el) => {
        own.current = el;
        if (typeof buttonRef === 'function') buttonRef(el);
        else if (buttonRef) buttonRef.current = el;
      }}
      type={type}
      class={`btn btn-${variant} btn-${size}${children ? '' : ' btn-icon'}${cls ? ` ${cls}` : ''}`}
      disabled={disabled}
      title={title}
      onClick={onClick}
      {...aria}
    >
      {icon && <Icon name={icon} size={size === 'lg' ? 22 : size === 'sm' ? 15 : 18} filled={icon === 'play'} />}
      {children && <span class="btn-label">{children}</span>}
      {hint && <kbd class="btn-hint">{hint}</kbd>}
    </button>
  );
}
